const FREQUENCY_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};

export function parseWipesPlanFrequency(rawName) {
  const name = String(rawName ?? '')
    .toLowerCase()
    .replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xFEE0));
  let best = null;
  for (const unit of ['month', 'week', 'day']) {
    for (const match of name.matchAll(new RegExp(`(\\d+)\\s*(?:${FREQUENCY_WORDS[unit]})`, 'g'))) {
      if (!best || match.index > best.index) {
        best = { unit, count: Number.parseInt(match[1], 10), index: match.index };
      }
    }
  }
  if (best) return { unit: best.unit, count: best.count };
  for (const unit of ['month', 'week', 'day']) {
    if (new RegExp(FREQUENCY_WORDS[unit]).test(name)) return { unit, count: 1 };
  }
  return null;
}

export function parseWipesSubscriptionConfig(rawConfig) {
  const [unit = '', intervalList = '', defaultInterval = ''] = String(rawConfig ?? '').split('|');
  const intervals = [...new Set(
    intervalList.split(',').map(Number).filter((value) => Number.isSafeInteger(value) && value > 0),
  )];
  if (!unit || intervals.length === 0) return null;
  const normalizedDefault = Number(defaultInterval);
  return {
    unit,
    intervals,
    defaultInterval: intervals.includes(normalizedDefault) ? normalizedDefault : intervals[0],
  };
}

/**
 * Wipes-specific entitlement: a product-level plan is offered only when its
 * localized cadence matches the variant metafield configuration and Shopify
 * supplied an allocation for that exact variant/plan pair.
 */
export function resolveConfiguredPlanSet(model, variantId, selectedPlanId = null) {
  const variant = model.variantById(variantId);
  if (!variant) return { plans: [], selectedPlanId: null };
  const config = parseWipesSubscriptionConfig(variant.subscriptionConfig);
  if (!config) return { plans: [], selectedPlanId: null };

  const plans = config.intervals
    .map((interval) => {
      const matches = model.contract.plans.filter((plan) => {
        const frequency = parseWipesPlanFrequency(plan.name);
        return frequency?.unit === config.unit
          && frequency.count === interval
          && model.allocationFor(variant.id, plan.id);
      });
      return matches.length === 1 ? matches[0] : null;
    })
    .filter(Boolean);

  const retained = plans.find((plan) => plan.id === String(selectedPlanId ?? ''));
  const configuredDefault = plans.find((plan) => {
    const frequency = parseWipesPlanFrequency(plan.name);
    return frequency?.count === config.defaultInterval;
  });
  return {
    plans,
    selectedPlanId: retained?.id ?? configuredDefault?.id ?? plans[0]?.id ?? null,
  };
}

/** Honor an explicit initial URL plan only for the exact selected variant/cadence. */
export function resolveInitialSellingPlanId(model, variantId, search) {
  if (!model?.variantById(variantId)) return null;
  const params = new URLSearchParams(String(search || ''));
  const plans = params.getAll('selling_plan');
  const variants = params.getAll('variant');
  if (plans.length !== 1 || !/^[1-9]\d*$/.test(plans[0])) return null;
  if (variants.length > 1 || (variants.length === 1 && variants[0] !== String(variantId))) return null;
  return resolveConfiguredPlanSet(model, variantId).plans
    .find((plan) => plan.id === plans[0])?.id ?? null;
}

// Compatibility export for the already-active Wipes rollout. New product
// migrations use the product-neutral name without disturbing that stable path.
export const resolveWipesPlanSet = resolveConfiguredPlanSet;

/** Shopper-entered daily use is an estimate, never a purchase or plan input. */
export function normalizeDailyUsage(value, fallback = 2) {
  const previous = Number.isFinite(Number(fallback)) ? Number(fallback) : 2;
  const number = value == null || String(value).trim() === '' ? previous : Number(value);
  return Math.min(100, Math.max(1, Math.round(Number.isFinite(number) ? number : previous)));
}

export function pdpUsageDuration(units, perDay, unit) {
  const divisor = { days: 1, weeks: 7, months: 30 }[unit];
  if (!(units > 0) || !(perDay > 0) || !divisor) return null;
  const duration = units / perDay / divisor;
  const precision = duration < 0.1 ? 100 : 10;
  return Math.round((duration + Number.EPSILON) * precision) / precision;
}

export function formatPdpUsageNumber(value, locale) {
  try { return new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: 2 }).format(value); }
  catch { return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(value); }
}

/** Display units follow the unrounded supply; this never selects a pack or plan. */
export function pdpUsageEstimate(units, perDay, maxUnit = 'months') {
  if (!Number.isSafeInteger(units) || units <= 0 || !Number.isFinite(perDay)
    || perDay < 1 || perDay > 100 || !['days', 'weeks', 'months'].includes(maxUnit)) return null;
  const days = units / perDay;
  const unit = maxUnit === 'days' || days < 7 ? 'days'
    : maxUnit === 'weeks' || days < 30 ? 'weeks' : 'months';
  const value = unit === 'weeks' ? Math.round(days / 7) : pdpUsageDuration(units, perDay, unit);
  return value > 0 ? { unit, value } : null;
}

export function formatPdpUsageDuration(estimate, locale) {
  if (!estimate) return '';
  const unit = { days: 'day', weeks: 'week', months: 'month' }[estimate.unit];
  if (!unit || !(estimate.value > 0)) return '';
  const options = { style: 'unit', unit, unitDisplay: 'long', maximumFractionDigits: 2 };
  try { return new Intl.NumberFormat(locale || 'en', options).format(estimate.value); }
  catch { return new Intl.NumberFormat('en', options).format(estimate.value); }
}

/** Keep existing per-item price math; only its neighboring supply text adapts. */
export function pdpUsagePriceText(buyBox, { price, unit, units, perDay, maxUnit, locale, cents }) {
  const panel = buyBox.querySelector('[data-pogi-usage-estimator]');
  const template = cents
    ? panel?.dataset.usageCentsTemplate || '[[price]]¢/[[unit]] · ~[[duration]]'
    : panel?.dataset.usageMoneyTemplate || '[[price]]/[[unit]] · ~[[duration]]';
  const duration = formatPdpUsageDuration(pdpUsageEstimate(units, perDay, maxUnit), locale);
  if (!duration || !['price', 'unit', 'duration'].every(key => template.includes(`[[${key}]]`))) return '';
  return template.split('[[price]]').join(price).split('[[unit]]').join(unit).split('[[duration]]').join(duration);
}

export function bindPdpUsage(buyBox) {
  const input = buyBox.querySelector('[data-pogi-daily-use]');
  input?.addEventListener('change', () => {
    if (input.value.trim() !== '' && Number.isFinite(Number(input.value))) buyBox.pogiDailyUsageEdited = true;
    input.value = String(buyBox.setDailyUsage(input.value));
  }, { signal: buyBox.v2Events.signal });
  const panel = buyBox.querySelector('[data-pogi-wipes-routine]');
  if (!panel) return;
  const signal = buyBox.v2Events.signal;
  const details = panel.querySelector('[data-pogi-routine-details]');
  const apply = panel.querySelector('[data-pogi-routine-apply]');
  const fields = [...panel.querySelectorAll('[data-pogi-routine-field]')];
  for (const field of fields) {
    const key = field.dataset.pogiRoutineField;
    if (buyBox.pogiRoutineDraft?.[key] !== undefined) field.value = buyBox.pogiRoutineDraft[key];
    field.addEventListener('input', () => {
      buyBox.pogiRoutineDraft = Object.fromEntries(fields.map((item) => [item.dataset.pogiRoutineField, item.value]));
      renderWipesRoutine(buyBox, panel);
    }, { signal });
    field.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      event.preventDefault();
      apply?.click();
    }, { signal });
  }
  if (details && buyBox.pogiRoutineOpen !== undefined) details.open = buyBox.pogiRoutineOpen;
  details?.addEventListener('toggle', () => { buyBox.pogiRoutineOpen = details.open; }, { signal });
  apply?.addEventListener('click', () => {
    const estimate = readWipesRoutine(panel);
    if (!estimate) return;
    buyBox.pogiDailyUsageEdited = true;
    buyBox.setDailyUsage(estimate.daily);
    if (details) details.open = false;
    buyBox.pogiRoutineOpen = false;
    input?.focus();
  }, { signal });
  const starter = panel.querySelector('[data-pogi-starter-apply]');
  starter?.addEventListener('click', async () => {
    if (buyBox.pogiStarterPending) return;
    buyBox.pogiStarterPending = true;
    const status = panel.querySelector('[data-pogi-starter-status]');
    if (status) status.textContent = '';
    renderWipesRoutine(buyBox, panel);
    try {
      const applied = await buyBox.applyStarterPackRecommendation?.();
      if (signal.aborted) return;
      if (applied) panel.querySelector('[data-pogi-starter-details]').open = false;
      else if (status) status.textContent = panel.dataset.starterFailed || '';
    } catch {
      if (!signal.aborted && status) status.textContent = panel.dataset.starterFailed || '';
    } finally {
      buyBox.pogiStarterPending = false;
      const currentPanel = buyBox.querySelector('[data-pogi-wipes-routine]');
      if (currentPanel) renderWipesRoutine(buyBox, currentPanel);
    }
  }, { signal });
}

/** Explicit planning inputs, not a recommended cleaning frequency. */
export function wipesRoutineEstimate(values) {
  const result = {};
  for (const key of ['dogs', 'cleanups', 'wipes']) {
    const raw = values?.[key];
    const value = raw == null || String(raw).trim() === '' ? NaN : Number(raw);
    if (!Number.isInteger(value) || value < 1 || value > 10) return null;
    result[key] = value;
  }
  result.daily = result.dogs * result.cleanups * result.wipes;
  return result.daily <= 100 ? result : null;
}

function readWipesRoutine(panel) {
  return wipesRoutineEstimate(Object.fromEntries([...panel.querySelectorAll('[data-pogi-routine-field]')]
    .map((field) => [field.dataset.pogiRoutineField, field.value])));
}

function renderWipesRoutine(buyBox, panel) {
  if (!panel) return;
  const estimate = readWipesRoutine(panel);
  const locale = buyBox.v2Contract?.market?.displayLocale;
  const output = panel.querySelector('[data-pogi-routine-result]');
  if (output) {
    output.textContent = estimate ? Object.entries(estimate).reduce((text, [key, value]) =>
      text.split(`[[${key}]]`).join(formatPdpUsageNumber(value, locale)), panel.dataset.routineTemplate || '')
      : panel.dataset.routineInvalid || '';
  }
  const apply = panel.querySelector('[data-pogi-routine-apply]');
  if (apply) apply.disabled = !estimate;
  const starter = panel.querySelector('[data-pogi-starter-apply]');
  if (starter) {
    const recommendation = buyBox.getStarterPackRecommendation?.();
    starter.disabled = !recommendation || Boolean(buyBox.pogiStarterPending);
    starter.textContent = recommendation ? (panel.dataset.starterTemplate || '').split('[[pack]]').join(recommendation.label)
      : panel.dataset.starterUnavailable || '';
  }
}

/** One source for the selected-quantity estimate beside packs and intervals. */
export function renderPdpUsage(buyBox, { enabled, unitsPerPack, quantity, perDay, subscribed, locale, maxUnit }) {
  const panel = buyBox.querySelector('[data-pogi-usage-estimator]');
  if (!panel) return;
  const template = panel.dataset.usageTemplate || '';
  const units = unitsPerPack * quantity;
  const estimate = pdpUsageEstimate(units, perDay, maxUnit || panel.dataset.usageUnit);
  const valid = enabled && Number.isSafeInteger(unitsPerPack) && unitsPerPack > 0
    && Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(units)
    && estimate
    && ['units', 'duration'].every((key) => template.includes(`[[${key}]]`));
  const durationText = formatPdpUsageDuration(estimate, locale);
  const summary = valid ? template
    .split('[[units]]').join(formatPdpUsageNumber(units, locale))
    .split('[[duration]]').join(durationText) : '';
  const output = panel.querySelector('[data-pogi-usage-summary]');
  if (output) output.textContent = summary;
  panel.hidden = !summary;
  const monthNote = panel.querySelector('[data-pogi-usage-month-note]');
  if (monthNote) monthNote.hidden = !summary || estimate?.unit !== 'months';
  const quantityNote = panel.querySelector('[data-pogi-usage-quantity]');
  if (quantityNote) {
    quantityNote.textContent = summary ? (quantityNote.dataset.quantityTemplate || '')
      .split('[[quantity]]').join(formatPdpUsageNumber(quantity, locale)) : '';
    quantityNote.hidden = !summary || quantity === 1;
  }
  const input = panel.querySelector('[data-pogi-daily-use]');
  if (input && input.ownerDocument.activeElement !== input) input.value = String(perDay);
  const note = panel.querySelector('[data-pogi-usage-mode-note]');
  if (note) note.hidden = !subscribed;
  const example = panel.querySelector('[data-pogi-usage-example]');
  if (example) {
    example.hidden = Boolean(buyBox.pogiDailyUsageEdited);
    const editedNote = panel.querySelector('[data-pogi-usage-edited]');
    if (editedNote) editedNote.hidden = !buyBox.pogiDailyUsageEdited;
  }
  renderWipesRoutine(buyBox, panel.querySelector('[data-pogi-wipes-routine]'));
  const interval = buyBox.querySelector('[data-pogi-usage]');
  if (interval) {
    interval.textContent = subscribed ? summary : '';
    interval.hidden = !subscribed || !summary;
  }
}

/** An example calculation only: never chooses a selling plan or delivery interval. */
export function renderSubscriptionUsage(element, { enabled, unitsPerPack, quantity = 1, perDay, durationUnit = 'days', template, locale }) {
  if (!element) return;
  const units = unitsPerPack * quantity;
  let text = '';
  if (enabled && Number.isSafeInteger(unitsPerPack) && unitsPerPack > 0
    && Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(units)
    && Number.isFinite(perDay) && perDay > 0
    && ['days', 'weeks'].includes(durationUnit)
    && ['per_day', 'units', durationUnit].every((key) => String(template || '').includes(`[[${key}]]`))) {
    let numbers;
    try { numbers = new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: 2 }); }
    catch { numbers = new Intl.NumberFormat('en', { maximumFractionDigits: 2 }); }
    text = template;
    const duration = durationUnit === 'weeks' ? Math.round(units / perDay / 7) : units / perDay;
    for (const [key, value] of Object.entries({ per_day: perDay, units, [durationUnit]: duration })) {
      text = text.split(`[[${key}]]`).join(numbers.format(value));
    }
  }
  element.textContent = text;
  element.hidden = !text;
}

/** The builder pins one variant per row. Read its actual count option, not merchant card copy. */
export function subscriptionUsageForProduct(handle, options) {
  const family = {
    'pogis-dog-wipes': 'wipes',
    'pogis-dog-training-pads': 'pads',
    'pogis-dog-poop-bags': 'bags',
    'pogis-compostable-dog-poop-bags': 'bags',
  }[handle];
  if (!family || !Array.isArray(options)) return null;
  const candidates = options.map((option) => {
    const normalized = String(option).replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xFEE0));
    // Current packs have ungrouped whole counts. Ambiguous decimal/thousands
    // punctuation must suppress the example rather than silently change its math.
    if (/\d[.,，．]\d|\d[ \u00a0\u202f]+\d{3}\b/.test(normalized)) return null;
    const numbers = (normalized.match(/\d+/g) || []).map(Number);
    // Pad dimensions contain two numbers; a rolls/bags count intentionally does too.
    if (numbers.length !== 1 && !(family === 'bags' && numbers.length === 2)) return null;
    return Math.max(...numbers);
  }).filter((count) => Number.isSafeInteger(count) && count > 0);
  if (candidates.length !== 1) return null;
  return { family, unitsPerPack: candidates[0], perDay: family === 'wipes' ? 4 : 2 };
}
